import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Clock, User } from "lucide-react";
import { useConversations } from "@/hooks/useConversations";
import { useMessages } from "@/hooks/useMessages";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";

interface ConversationListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const ConversationList = ({ selectedId, onSelect }: ConversationListProps) => {
  const { conversations, loading } = useConversations();
  const [sentimentFilter, setSentimentFilter] = useState<string>("all");
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-accent/10 text-accent border-accent/20";
      case "waiting":
        return "bg-yellow-500/10 text-yellow-600 border-yellow-500/20";
      case "closed":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Loading conversations...
      </div>
    );
  }

  const filteredConversations = sentimentFilter === "all" 
    ? conversations 
    : conversations.filter(c => c.sentiment_trend === sentimentFilter);

  return (
    <div className="space-y-2">
      <div className="px-2">
        <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Filter by sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conversations</SelectItem>
            <SelectItem value="positive">😊 Positive</SelectItem>
            <SelectItem value="neutral">😐 Neutral</SelectItem>
            <SelectItem value="negative">😟 Negative</SelectItem>
            <SelectItem value="urgent">🚨 Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="overflow-y-auto max-h-[550px]">
        {filteredConversations.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No conversations match this filter
          </div>
        ) : (
          filteredConversations.map((conversation) => {
          // Get unread count (simplified - in production, track this properly)
          const unreadCount = 0;
          
          return (
            <button
              key={conversation.id}
              onClick={() => onSelect(conversation.id)}
              className={cn(
                "w-full p-4 border-b hover:bg-muted/50 transition-colors text-left",
                selectedId === conversation.id && "bg-primary/5 border-l-4 border-l-primary"
              )}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-sm truncate">
                      {conversation.customer_name || conversation.customer_phone}
                    </h4>
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs">
                        {unreadCount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{conversation.customer_phone}</p>
                </div>
                <div className="flex gap-1">
                  {conversation.sentiment_trend && (
                    <Badge variant="outline" className={cn("text-xs",
                      conversation.sentiment_trend === 'positive' && "bg-green-500/10 text-green-600 border-green-500/20",
                      conversation.sentiment_trend === 'neutral' && "bg-gray-500/10 text-gray-600 border-gray-500/20",
                      conversation.sentiment_trend === 'negative' && "bg-red-500/10 text-red-600 border-red-500/20",
                      conversation.sentiment_trend === 'urgent' && "bg-orange-500/10 text-orange-600 border-orange-500/20"
                    )}>
                      {conversation.sentiment_trend === 'positive' && '😊'}
                      {conversation.sentiment_trend === 'neutral' && '😐'}
                      {conversation.sentiment_trend === 'negative' && '😟'}
                      {conversation.sentiment_trend === 'urgent' && '🚨'}
                    </Badge>
                  )}
                  <Badge variant="outline" className={cn("text-xs", getStatusColor(conversation.status))}>
                    {conversation.status}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {format(new Date(conversation.last_message_at), 'p')}
                </div>
              </div>
            </button>
          );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationList;
